import { Anchor, Badge, Button, Group, Select, Table, Text } from '@mantine/core';
import { IconCalendarEvent, IconCarGarage, IconCircleDot, IconListDetails, IconPlus } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { casIzMs, datum, denar } from '../oblika/oblika';
import { GlavaZaslona, IskalnoPolje, Seznam, Stolpec, ZaslonSeznama } from '../seznam/Seznam';
import { useSeznam } from '../seznam/useSeznam';
import { STATUSI, type StatusNaloga } from './delavnica';
import { usePravica } from '../seja/pravice';

type Razvrsti = 'stevilka' | 'sprejeto' | 'status' | 'vozilo' | 'kupec' | 'mehanik' | 'predviden' | 'skupaj';

/** Izbira statusa: privzeto odprti nalogi (še niso prevzeti), »Vsi« ali en status. */
const IZBIRA_STATUSA = [
  { value: 'ODPRTI', label: 'Odprti' },
  { value: 'VSI', label: 'Vsi' },
  ...(Object.keys(STATUSI) as StatusNaloga[]).map((s) => ({ value: s, label: STATUSI[s].naziv })),
];

/**
 * Delovni nalogi (modul Delavnica): iskanje po številki, registrski, vozilu, stranki ali težavi, filter statusa
 * (privzeto odprti), razvrščanje in strani na strežniku. Klik na vrstico odpre nalog, »Sprejem vozila« nov nalog.
 */
export function PregledNalogov() {
  const ureja = usePravica('DELAVNICA', 'UREJANJE');
  const seznam = useSeznam<Razvrsti>();
  const navigate = useNavigate();
  const status = seznam.parameter('status') ?? 'ODPRTI';
  const poizvedba = useQuery({
    queryKey: ['delovni-nalogi', seznam.iskanje, status, seznam.zaApi],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/delavnica/nalogi/pregled', {
          signal,
          params: {
            query: {
              iskanje: seznam.iskanje || undefined,
              status: status === 'VSI' ? undefined : status,
              ...seznam.zaApi,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const vrstice = poizvedba.data?.vsebina ?? [];

  return (
    <ZaslonSeznama>
      <GlavaZaslona
        naslov="Delovni nalogi"
        opis="Vozila v delavnici od sprejema do prevzema."
        pomoc="delovni-nalogi"
        dejanja={
          <Group gap="xs">
            <Button
              component={Link}
              to="/delavnica/cenik"
              variant="default"
              leftSection={<IconListDetails size={18} />}
            >
              Cenik
            </Button>
            <Button
              component={Link}
              to="/delavnica/termini"
              variant="default"
              leftSection={<IconCalendarEvent size={18} />}
            >
              Termini
            </Button>
            <Button component={Link} to="/delavnica/vozila" variant="default" leftSection={<IconCarGarage size={18} />}>
              Vozila strank
            </Button>
            <Button component={Link} to="/delavnica/gume" variant="default" leftSection={<IconCircleDot size={18} />}>
              Hotel za gume
            </Button>
            {ureja && (
              <Button component={Link} to="/delavnica/sprejem" leftSection={<IconPlus size={18} />}>
                Sprejem vozila
              </Button>
            )}
          </Group>
        }
      />
      <Seznam
        seznam={seznam}
        filtri={
          <>
            <IskalnoPolje seznam={seznam} namig="Išči po številki, registrski, vozilu, stranki ali težavi" />
            <Select
              label="Status"
              data={IZBIRA_STATUSA}
              value={status}
              onChange={(v) => seznam.nastavi({ status: !v || v === 'ODPRTI' ? null : v })}
              allowDeselect={false}
              w={{ base: '100%', sm: 180 }}
            />
          </>
        }
        glava={
          <>
            <Stolpec seznam={seznam} kljuc="stevilka" naziv="Št. naloga" />
            <Stolpec seznam={seznam} kljuc="sprejeto" naziv="Sprejeto" />
            <Stolpec seznam={seznam} kljuc="status" naziv="Status" />
            <Stolpec seznam={seznam} kljuc="vozilo" naziv="Vozilo" />
            <Stolpec seznam={seznam} kljuc="kupec" naziv="Stranka" />
            <Stolpec seznam={seznam} kljuc="mehanik" naziv="Mehanik" />
            <Stolpec seznam={seznam} kljuc="predviden" naziv="Predviden zaključek" />
            <Stolpec seznam={seznam} kljuc="skupaj" naziv="Skupaj" desno />
          </>
        }
        vrstice={vrstice.map((n, i) => (
          <Table.Tr key={n.id} style={{ cursor: 'pointer' }} onClick={() => void navigate(`/delavnica/nalogi/${n.id}`)}>
            <Table.Td c="dimmed" className="brez-preloma">
              {seznam.zamik + i + 1}
            </Table.Td>
            <Table.Td>
              <Anchor
                component={Link}
                to={`/delavnica/nalogi/${n.id}`}
                onClick={(e) => e.stopPropagation()}
                fw={600}
                className="brez-preloma"
              >
                {n.stevilka}
              </Anchor>
            </Table.Td>
            <Table.Td className="brez-preloma">{casIzMs(n.sprejeto)}</Table.Td>
            <Table.Td>
              {n.status && (
                <Badge color={STATUSI[n.status].barva} variant="light" className="brez-preloma">
                  {STATUSI[n.status].naziv}
                </Badge>
              )}
            </Table.Td>
            <Table.Td>
              <Text size="sm" fw={600} className="brez-preloma">
                {n.registrska}
              </Text>
              <Text size="xs" c="dimmed">
                {n.vozilo}
              </Text>
            </Table.Td>
            <Table.Td>{n.kupec}</Table.Td>
            <Table.Td>{n.mehanik}</Table.Td>
            <Table.Td className="brez-preloma">{datum(n.predvidenZakljucek)}</Table.Td>
            <Table.Td ta="right" className="brez-preloma">
              {denar(n.skupaj)}
            </Table.Td>
          </Table.Tr>
        ))}
        stVrstic={vrstice.length}
        stStolpcev={8}
        skupaj={poizvedba.data?.skupaj ?? 0}
        nalaganje={poizvedba.isPending}
        osvezevanje={poizvedba.isFetching}
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        prazno="Ni delovnih nalogov za izbrane pogoje."
        napakaNaslov="Delovnih nalogov ni bilo mogoče naložiti"
      />
    </ZaslonSeznama>
  );
}
